import { _, _any } from "i18n";
import {
	Colors,
	type GamePlayerItemRequest,
	GPGameTeamResponse,
	GPPlayerPositionResponse,
	GameTeamPutColorsRequest,
	GameTeamSheetCompositionPlayersResponse,
	GPGamePlayerResponse,
	GTSCompositionPosition,
	GameTeamSheetGameTeamPlayersSearchResponse,
	GameTeamSheetPlayersResponse, GenderOptions,
} from "../../../types";
import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import SelectComponent from "ui-kit/src/SelectComponent/SelectComponent";
import Loader from "ui-kit/src/Loader/Loader";
import { colorSelectValues, getColorSelectValue } from "../../../functions";
import useTeamSheet from "./TeamSheetModalFunctions";
import PlayerTable from "./PlayerTable";
import { Shirt } from "../../assets/Shirt";
import { Shorts } from "../../assets/Shorts";
import { Button} from "react-aria-components";
import clsx from "clsx";

interface TeamSheetModalProps {
	canEditColors: boolean;
	selectedTeam: GPGameTeamResponse;
	handleTeamSheetEdit: (id: number, squad: GamePlayerItemRequest[], substitutes: GamePlayerItemRequest[], colors: GameTeamPutColorsRequest, saveAnyway: boolean, gender?: string | null) => void | Promise<void>;
	positions: GPPlayerPositionResponse[];
	loadCompositionPlayers: (teamId: number, composition: string) => Promise<GameTeamSheetCompositionPlayersResponse | null>;
	searchPlayers: (teamId: number, filter: string) => Promise<GameTeamSheetGameTeamPlayersSearchResponse | null>;
	isLockedForCurrentUser: boolean;
	loadClubPlayers: (teamId: number) => Promise<GameTeamSheetPlayersResponse | null>;
	showForceSave: boolean;
	canEditNumbers: boolean;
	canRemovePlayers: boolean;
	canAnonymizePlayers: boolean;
	canEditGender?: boolean;
}

export function TeamSheetModal({
	canEditColors,
	selectedTeam,
	handleTeamSheetEdit,
	positions,
	loadCompositionPlayers,
	searchPlayers,
	isLockedForCurrentUser,
	loadClubPlayers,
	showForceSave,
	canEditNumbers,
	canRemovePlayers,
	canAnonymizePlayers,
	canEditGender
   }: TeamSheetModalProps) {
	const {
		updatedPlayers,
		updatedSubstitutes,
		handlePlayerChange,
		handleSubstituteChange,
		handlePlayerSelect,
		handleSubstitutePlayerSelect,
		addSubstitute,
		setUpdatedPlayers,
		handleRemovePlayer,
	} = useTeamSheet(selectedTeam);

	const [composition, setComposition] = useState<string | number>();
	const [selectColors, setSelectColors] = useState<Colors>({
		shirt: selectedTeam?.colorShirt ?? undefined,
		shorts: selectedTeam?.colorShort ?? undefined,
	});
	const [selectedGender, setSelectedGender] = useState<string | undefined>(undefined);

	const [clubPlayers, setClubPlayers] = useState<{ value: number; label: string }[]>([]);
	const [isLoadingClubPlayers, setIsLoadingClubPlayers] = useState(true);
	const selectedTeamIdRef = useRef<number | null>(null);
	const [saveAnyway, setSaveAnyway] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const isSubmittingRef = useRef(false);

	useEffect(() => {
		if (selectedTeamIdRef.current !== selectedTeam.id) {
			selectedTeamIdRef.current = selectedTeam.id;

			loadClubPlayers(selectedTeam.id)
				.then(playersResponse => {
					if (playersResponse && playersResponse.players) {
						const formattedPlayers = playersResponse.players.map(player => ({
							value: player.id,
							label: `${player.name} (${player.birthDay ?? '-'})`
						}));
						setClubPlayers(formattedPlayers);
					}
				})
				.finally(() => {
					setIsLoadingClubPlayers(false);
				});
		}
	}, [selectedTeam.id, loadClubPlayers]);
	const clubPlayerOptions = useMemo(() => clubPlayers, [clubPlayers]);

	const positionsOptions = useMemo(
		() => positions.map((position) => ({
			value: position.value,
			label: position.title,
		})),
		[positions]
	);

	const teamSheetFormationOptions = useMemo(
		() => selectedTeam.teamSheetFormations.map((formation) => ({
			value: formation.value,
			label: formation.name,
		})),
		[selectedTeam.teamSheetFormations]
	);

	const onColorChange = useCallback((type: keyof Colors, value: { value: string | number; label: string } | null) => {
		setSelectColors((prev) => ({ ...prev, [type]: value?.value }));
	}, []);

	const onCompositionChange = useCallback((value: { value: string | number; label: string } | null) => {
		if (value) setComposition(value.value);
	}, []);

	const onGenderChange = useCallback((value: { value: string | number; label: string } | null) => {
		setSelectedGender(value ? String(value.value) : undefined);
	}, []);

	const handleSaveAnyway = (value: boolean) => {
		setSaveAnyway(value);
	}

	const handleSubmitTeamSheet = useCallback(async () => {
		if (isSubmittingRef.current) {
			return;
		}
		isSubmittingRef.current = true;
		setIsSubmitting(true);

		try {
			const squad: GamePlayerItemRequest[] = updatedPlayers.map(player => ({
				position: player.position,
				name: player.name,
				number: player.numberSign ?? null,
				goals: player.goals || null,
				idPlayer: player.playerId ? String(player.playerId) : null,
				id: player.id ? String(player.id) : null,
				valid: true,
				substitute: false,
				discover: false,
				discovery: false,
				isAnonymized: player.isAnonymized,
			}));

			const substitutes: GamePlayerItemRequest[] = updatedSubstitutes.map(player => ({
				position: player.position,
				name: player.name,
				number: player.numberSign ?? null,
				goals: player.goals || null,
				idPlayer: player.playerId ? String(player.playerId) : null,
				id: player.id ? String(player.id) : null,
				valid: true,
				substitute: true,
				discover: false,
				discovery: false,
				isAnonymized: player.isAnonymized,
			}));

			const colors: GameTeamPutColorsRequest = {
				colorShort: selectColors.shorts,
				colorShirt: selectColors.shirt,
			};

			await handleTeamSheetEdit(
				selectedTeam.id,
				squad,
				substitutes,
				colors,
				saveAnyway,
				selectedGender ?? null,
			);
		} finally {
			isSubmittingRef.current = false;
			setIsSubmitting(false);
		}
	}, [
		handleTeamSheetEdit,
		selectedTeam.id,
		updatedPlayers,
		updatedSubstitutes,
		selectColors,
		saveAnyway,
		selectedGender,
	]);

	const mapCompositionToPlayers = (compositionPlayers: GTSCompositionPosition[], existingPlayers: GPGamePlayerResponse[]) => {
		const updatedPlayers = [...existingPlayers];

		compositionPlayers.forEach(({ position, players }) => {
			players.forEach((player) => {
				const index = player.displayOrder - 1;

				if (index >= 0 && index < updatedPlayers.length) {
					updatedPlayers[index] = {
						...updatedPlayers[index],
						playerId: player.id,
						position: position,
						id: updatedPlayers[index].id ?? null,
					};
				}
			});
		});

		return updatedPlayers;
	};

	const loadComposition = async () => {
		if (composition) {
			const response = await loadCompositionPlayers(selectedTeam.id, String(composition));
			if (response && response.compositionPlayers) {
				const newPlayers = mapCompositionToPlayers(response.compositionPlayers, updatedPlayers);
				setUpdatedPlayers(newPlayers);
			}
		}
	};

	return isLockedForCurrentUser ? (
		<div className="team-sheet-modal__message">
			<p>{_('matchSheet.locked.text')}</p>
		</div>
	) : (
		<>
			<table className="team-sheet-modal__player-table">
				<tbody>
					{canEditColors && <tr>
						<td>
							<div className="selectContainer-header">
								<div className="selectLabel">
									<div className="jersey">
										<div className="colorShirt" style={{ color: selectColors.shirt ?? "grey" }}>
											<Shirt />
										</div>
										{_("app.coach.match.shirt_title")}
									</div>
								</div>
								<SelectComponent
									value={getColorSelectValue(selectColors.shirt)}
									handleChange={(value) => onColorChange("shirt", value)}
									items={colorSelectValues(_any)}
									isCleareable={false}
								/>
							</div>
						</td>
						<td>
							<div className="selectContainer-header">
								<div className="selectLabel">
									<div className="jersey">
										<div className="colorShirt" style={{ color: selectColors.shorts ?? "grey" }}>
											<Shorts />
										</div>
										{_("app.coach.match.shorts_title")}
									</div>
								</div>
								<SelectComponent
									value={getColorSelectValue(selectColors.shorts)}
									handleChange={(value) => onColorChange("shorts", value)}
									items={colorSelectValues(_any)}
									isCleareable={false}
								/>
							</div>
						</td>
					</tr>}
					{canEditColors &&
					<tr>
						<td>
							<div className="selectContainer-header">
								<div className="selectLabel">{_("matchSheet.composition")}</div>
								<SelectComponent
									handleChange={onCompositionChange}
									items={teamSheetFormationOptions}
									isCleareable={false}
								/>
								<a href="#" className="btn team-sheet-modal__auto-load_btn" onClick={loadComposition} >
									{_('matchSheet.button.add.loadteam')}
								</a>
							</div>
						</td>
					</tr>}

					{canEditGender &&
						<tr>
							<td>
								<div className="selectContainer-header">
									<div className="selectLabel">{_("matchSheet.gender")}</div>
									<SelectComponent
										handleChange={onGenderChange}
										items={GenderOptions}
										isCleareable={true}
									/>
								</div>
							</td>
						</tr>}
				</tbody>
			</table>

			<div className="team-sheet-modal__title">
				<h2>{_('matchSheet.title.workforce')}</h2>
				{canAnonymizePlayers && <div>
					<span
						className="team-sheet-modal__anonymize-handle"
						onClick={() => {
							updatedPlayers.forEach((p, i) => {
								handlePlayerChange(i, "isAnonymized", true)
							})
							updatedSubstitutes.forEach((s, i) => {
								handleSubstituteChange(i, "isAnonymized", true)
							})
						}}>{_("matchSheet.table.anonymize.all")}</span>
					<span
						className="team-sheet-modal__anonymize-handle"
						onClick={() => {
							updatedPlayers.forEach((p, i) => {
								handlePlayerChange(i, "isAnonymized", false)
							})
							updatedSubstitutes.forEach((s, i) => {
								handleSubstituteChange(i, "isAnonymized", false)
							})
						}}>{_("matchSheet.table.deanonymize.all")}</span>
				</div>}
			</div>

			<PlayerTable
				players={updatedPlayers}
				positionsOptions={positionsOptions}
				clubPlayerOptions={clubPlayerOptions}
				handlePlayerChange={handlePlayerChange}
				handlePlayerSelect={handlePlayerSelect}
				handleRemovePlayer={handleRemovePlayer}
				searchPlayers={searchPlayers}
				selectedTeam={selectedTeam}
				isLoadingClubPlayers={isLoadingClubPlayers}
				canEditNumbers={canEditNumbers}
				canRemovePlayers={canRemovePlayers}
				canAnonymizePlayers={canAnonymizePlayers}
				supportsPlainTeamSheet={selectedTeam.permissions.supportsPlainTeamSheet}
			/>

			<div className="team-sheet-modal__title">
				<h2>{_('matchSheet.title.replacements')}</h2>
			</div>

			<PlayerTable
				players={updatedSubstitutes}
				positionsOptions={positionsOptions}
				clubPlayerOptions={clubPlayerOptions}
				handlePlayerChange={handleSubstituteChange}
				handlePlayerSelect={handleSubstitutePlayerSelect}
				handleRemovePlayer={handleRemovePlayer}
				searchPlayers={searchPlayers}
				selectedTeam={selectedTeam}
				isLoadingClubPlayers={isLoadingClubPlayers}
				canEditNumbers={canEditNumbers}
				canRemovePlayers={canRemovePlayers}
				canAnonymizePlayers={canAnonymizePlayers}
				supportsPlainTeamSheet={selectedTeam.permissions.supportsPlainTeamSheet}
			/>

			<button className="btn" onClick={addSubstitute}>
				{_('matchSheet.button.add.replacement')}
			</button>

			<div className="team-sheet-modal__footer">

				{showForceSave &&
					<div className="team-sheet-modal__force-save">
						<input id="saveAnyway" type="checkbox" onClick={() => handleSaveAnyway(!saveAnyway)} />
						<label htmlFor="saveAnyway">{_('matchSheet.label.saveAnyway')}</label>
					</div>
				}

				{isSubmitting === false ?
					<Button
						className={clsx("button")}
						isDisabled={isSubmitting}
						onPress={() => handleSubmitTeamSheet()}
					>
						{_('matchSheet.label.valid_edit')}
					</Button> : <Loader />
				}
			</div>
		</>
	);
}
